import I18n from 'I18n';
import { memo, useState, useEffect, useMemo } from 'react';
import CampaignSelectFormControl from 'campaigns-lib/components/campaignSelect/CampaignSelectFormControl';
import { useFetchCampaignsAssociationSettings, hasCampaignsReadAccess, hasCampaignsWriteAccess } from './hooks/useFetchCampaignsAssociationSettings';
import { useFetchAssociatedCampaigns } from './hooks/useFetchAssociatedCampaign';
import { useHandleCampaignChange } from './hooks/useHandleCampaignChange';
import { useFetchObjectPropertyDefinitions } from './hooks/useFetchObjectPropertyDefinitions';
import CampaignsPlatformSelectLabel from './CampaignsPlatformSelectLabel';
import { useCampaignsContext } from './CampaignsProvider';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { useFetchAssociationDefinition } from './hooks/useFetchAssociationDefinition';
import { useFetchAssociatedAssetObjectId } from './hooks/useFetchAssociatedAssetObjectId';
import { userInfoSync } from 'hub-http/userInfo';
import { Scopes } from 'campaigns-lib/constants/Permissions';
import { arrayContainsSameValues, formatAssetLabel } from 'campaigns-lib/utils/campaignSelect';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
// @ts-expect-error has no typings yet
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function CampaignsPlatformSelect({
  objectType,
  assetId,
  crmIdProperty = 'hs_object_id',
  onChange = () => {},
  save = true,
  editorSelect = true,
  FormLabel = undefined,
  withUIFloatingAlertList = true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  disabled = false,
  disabledTooltip,
  formControlStyle,
  objectBuilderUiProps,
  showMultiCampaignTip
}) {
  // To ensure the select is not removed when we invalidate the initial campaign cache on
  // change for the other campaign platform component to automatically update.
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  const {
    setIsOneToMany
  } = useCampaignsContext();
  const [selectedCampaignGuids, setSelectedCampaignGuids] = useState([]);
  const hasCampaignsToolAccess = useMemo(() => {
    try {
      return userInfoSync().user.scopes.includes(Scopes.CAMPAIGNS);
    } catch (error) {
      // Rely on BE to return the correct value
      return true;
    }
  }, []);
  const {
    data: associationSettings
  } = useFetchCampaignsAssociationSettings();
  const {
    data: objectDefinition
  } = useFetchObjectPropertyDefinitions({
    objectType
  });
  const {
    data: associationDefinition
  } = useFetchAssociationDefinition({
    associationSettings,
    objectType
  });
  const isOneToMany = (associationDefinition === null || associationDefinition === void 0 ? void 0 : associationDefinition.cardinality) === 'ONE_TO_MANY';
  const {
    data: objectId
  } = useFetchAssociatedAssetObjectId({
    objectType,
    assetId,
    crmIdProperty,
    skip: !hasCampaignsToolAccess
  });
  const {
    data: associatedCampaigns
  } = useFetchAssociatedCampaigns({
    objectType,
    objectId,
    associationSettings,
    isOneToMany,
    skip: !hasCampaignsToolAccess
  });
  useEffect(() => {
    if (associatedCampaigns) {
      setSelectedCampaignGuids(prev => {
        const fetchedCampaigns = associatedCampaigns.map(campaign => campaign.properties.hs_origin_asset_id.value);

        //Don't change if local state and fetched values are the same
        const isSame = arrayContainsSameValues(prev, fetchedCampaigns);
        if (isSame) return prev;

        //if they do differ, maintain the order of the elements currently in local state
        const existingInOrder = prev.filter(p => fetchedCampaigns.includes(p));
        const newItems = fetchedCampaigns.filter(f => !prev.includes(f));
        return [...existingInOrder, ...newItems];
      });
      setHasEverLoaded(true);
    } else {
      setSelectedCampaignGuids([]);
    }
  }, [associatedCampaigns]);
  useEffect(() => {
    setIsOneToMany(isOneToMany);
  }, [isOneToMany, setIsOneToMany]);

  // Automatically save the selected campaign when it changes.
  const handleCampaignChange = useHandleCampaignChange({
    objectType,
    onChange,
    save,
    selectedCampaignGuids,
    selectedAssetCrmId: objectId,
    setSelectedCampaignGuids,
    onSuccess: params => {
      var _params$updatedCampai, _params$selectedOptio, _params$selectedOptio2;
      const asset = objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.singularForm;
      const updatedGuids = (_params$updatedCampai = params === null || params === void 0 ? void 0 : params.updatedCampaignGuids) !== null && _params$updatedCampai !== void 0 ? _params$updatedCampai : [];
      const campaignName = (_params$selectedOptio = params === null || params === void 0 || (_params$selectedOptio2 = params.selectedOption) === null || _params$selectedOptio2 === void 0 ? void 0 : _params$selectedOptio2.text) !== null && _params$selectedOptio !== void 0 ? _params$selectedOptio : '';
      const additionalCampaigns = Math.max(updatedGuids.length - 1, 0);
      const isRemoval = selectedCampaignGuids.length > updatedGuids.length || campaignName === '';
      const titleKey = isRemoval ? 'campaignSelect.success.platformRemoveTitle' : additionalCampaigns > 0 ? 'campaignSelect.success.platformAddToManyTitle' : 'campaignSelect.success.platformAddTitle';
      const messageKey = isRemoval ? 'campaignSelect.success.platformRemoveMessage' : 'campaignSelect.success.platformAddMessage';
      FloatingAlertStore.addAlert({
        message: I18n.text(messageKey, {
          asset
        }),
        titleText: I18n.text(titleKey, {
          asset,
          campaignName,
          additionalCampaigns
        }),
        type: 'info'
      });
    },
    onFailure: () => {
      FloatingAlertStore.addAlert({
        message: I18n.text('campaignSelect.failure.platformMessage', {
          asset: formatAssetLabel(objectType, objectDefinition === null || objectDefinition === void 0 ? void 0 : objectDefinition.singularForm)
        }),
        titleText: I18n.text('campaignSelect.failure.platformTitle'),
        type: 'danger'
      });
    }
  });
  if (!associationSettings || !associationSettings[objectType] || !objectDefinition || !hasEverLoaded && assetId) {
    return null;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [withUIFloatingAlertList && /*#__PURE__*/_jsx(FloatingAlertList, {
      "data-test-id": "campaigns-lib-floating-alert-list"
    }), /*#__PURE__*/_jsx(CampaignSelectFormControl, {
      hasCampaignsReadAccess: hasCampaignsReadAccess(associationSettings, objectType),
      hasCampaignsWriteAccess: hasCampaignsWriteAccess(associationSettings, objectType),
      value: isOneToMany ? selectedCampaignGuids : selectedCampaignGuids[0],
      onChange: handleCampaignChange,
      assetObjectType: objectType,
      editorSelect: editorSelect,
      multi: isOneToMany,
      formControlProps: Object.assign({
        tooltip: isOneToMany ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "campaignSelect.oneToManyTooltip_jsx",
          options: {
            url: 'https://knowledge.hubspot.com/campaigns/understand-campaigns',
            use: 'link',
            useZorse: true,
            padding: '0'
          },
          elements: {
            Button: KnowledgeBaseButton
          }
        }) : undefined,
        tooltipPlacement: 'right',
        label: /*#__PURE__*/_jsx(CampaignsPlatformSelectLabel, {
          associationSettings: associationSettings,
          multi: isOneToMany,
          objectType: objectType
        }),
        FormLabel
      }, formControlStyle),
      formHelpAsParagraph: Boolean(FormLabel),
      helpAsTooltip: isOneToMany,
      assetLabel: objectDefinition.singularForm,
      disabled: disabled,
      disabledTooltip: disabledTooltip,
      objectBuilderUiProps: objectBuilderUiProps,
      showMultiCampaignTip: showMultiCampaignTip
    })]
  });
}
export default /*#__PURE__*/memo(CampaignsPlatformSelect);